import { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { api, type ProjectSummary } from "../api.ts";

function durationParts(seconds: number) {
  const total = Math.max(0, Math.round(seconds));
  const pad = (value: number) => String(value).padStart(2, "0");
  return {
    hours: pad(Math.floor(total / 3600)),
    minutes: pad(Math.floor((total % 3600) / 60)),
    seconds: pad(total % 60),
  };
}

export function Dashboard({ orgSlug }: { orgSlug: string }) {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      setProjects(await api.listProjects());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function onDelete(project: ProjectSummary) {
    if (!window.confirm(`Delete project "${project.name}"?\n\nAll media, fingerprints, API-key access, and built databases for this project will be permanently deleted. This cannot be undone.`)) {
      return;
    }
    setError(null);
    setDeleting(project.slug);
    try {
      await api.deleteProject(project.slug);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDeleting(null);
    }
  }

  async function onCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCreating(true);
    try {
      await api.createProject(name.trim());
      setName("");
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-8">
      <section>
        <h1 className="mb-1 text-2xl font-semibold text-slate-100">Offline Sdk</h1>
        <p className="text-sm text-slate-400">
          Fully offline detection — runs anywhere: mobile apps, edge devices, or your own servers. No network calls, no cloud lookups.
        </p>
      </section>

      <form onSubmit={onCreate} className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New project name"
          maxLength={64}
          className="flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
        />
        <button
          type="submit"
          disabled={creating || name.trim().length === 0}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
        >
          {creating ? "Creating…" : "Create project"}
        </button>
      </form>

      {error && <p className="text-sm text-rose-400">{error}</p>}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : projects.length === 0 ? (
        <p className="text-sm text-slate-500">No projects yet. Create one above.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => {
            const duration = durationParts(p.totalDuration);
            return (
            <div
              key={p.name}
              className="group relative rounded-xl border border-slate-800 bg-slate-900/60 transition hover:border-indigo-500/60 hover:bg-slate-900"
            >
              <button
                onClick={() => onDelete(p)}
                disabled={deleting === p.slug}
                title="Delete project"
                aria-label={`Delete project ${p.name}`}
                className="absolute right-2 top-2 z-10 rounded-md p-2 text-slate-500 opacity-0 transition hover:bg-rose-950/40 hover:text-rose-400 focus:opacity-100 group-hover:opacity-100 disabled:opacity-40"
              >
                <FaTrash size={14} />
              </button>
              <a
                href={`#/o/${encodeURIComponent(orgSlug)}/project/${encodeURIComponent(p.slug)}`}
                className="block p-5"
              >
                <div className="mb-2 truncate pr-8 text-base font-medium text-slate-100">{p.name}</div>
                <span
                  title={p.slug}
                  className="mb-4 inline-flex max-w-full items-center truncate rounded-full border border-indigo-400/25 bg-indigo-500/10 px-2.5 py-1 text-xs font-medium text-indigo-200"
                >
                  Slug: {p.slug}
                </span>
                <div className="flex items-end justify-between text-sm text-slate-400">
                  <span>{p.mediaCount} files</span>
                  <span
                    className="project-duration"
                    aria-label={`${Number(duration.hours)} hours, ${Number(duration.minutes)} minutes, ${Number(duration.seconds)} seconds`}
                  >
                    <span><b>{duration.hours}</b><small>hr</small></span>
                    <i>:</i>
                    <span><b>{duration.minutes}</b><small>min</small></span>
                    <i>:</i>
                    <span><b>{duration.seconds}</b><small>sec</small></span>
                  </span>
                </div>
              </a>
            </div>
          )})}
        </div>
      )}
    </div>
  );
}
